import { useEffect } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { Typography } from "@avenue-8/sidekick-ui";
import { PageLayout } from "@shared/layouts/PageLayout";
import { adminDashboardBreadcrumbs } from "@features/AdminDashboard/admin-dashboard-breadcrumbs";
import { useListDeliveries } from "@shared/generated/radar/queries/subscriptionAuditController/useListDeliveries";
import { CopyableText } from "@features/Users/components/CopyableText";
import {
  DeliveriesAuditFilters,
  type DeliveryStatusFilter,
} from "@features/EventSubscriptionsAudit/DeliveriesAuditFilters";
import { DeliveriesAuditList } from "@features/EventSubscriptionsAudit/DeliveriesAuditList";
import {
  CardMetaRow,
  TargetUrlText,
} from "@features/EventSubscriptionsAudit/EventSubscriptionsAudit.styles";

const PAGE_SIZE = 25;

// The audit API only lists subscriptions or their deliveries - no single-subscription
// lookup by id - so the subscriptions table passes name/targetUrl through navigation
// state. Direct URL access (refresh, deep link) just won't have it.
type LocationState = {
  subscriptionName?: string;
  targetUrl?: string;
  userName?: string | null;
} | null;

const STATUS_FILTER_VALUES: DeliveryStatusFilter[] = [
  "all",
  "pending",
  "succeeded",
  "failed",
];

export function SubscriptionDeliveriesAuditPage() {
  const { subscriptionId = "" } = useParams<{ subscriptionId: string }>();
  const location = useLocation();
  const state = location.state as LocationState;
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const statusParam = searchParams.get("status");
  const statusFilter: DeliveryStatusFilter = STATUS_FILTER_VALUES.includes(
    statusParam as DeliveryStatusFilter,
  )
    ? (statusParam as DeliveryStatusFilter)
    : "all";

  const query = useListDeliveries(subscriptionId, {
    page,
    pageSize: PAGE_SIZE,
    status: statusFilter === "all" ? undefined : statusFilter,
  });

  const deliveries = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const ownerUserId = deliveries[0]?.userId;

  const breadcrumbs = {
    current: { label: state?.subscriptionName ?? "Deliveries" },
    previous: [
      adminDashboardBreadcrumbs.dashboard,
      adminDashboardBreadcrumbs.eventSubscriptions,
    ],
  };

  // If a filter change shrinks the result set below the current page, snap
  // back to the last valid page instead of showing empty.
  useEffect(() => {
    if (query.isLoading) return;
    if (totalPages <= 0) return;
    if (page <= totalPages) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(totalPages));
        return next;
      },
      { replace: true },
    );
  }, [query.isLoading, page, setSearchParams, totalPages]);

  const handleStatusChange = (value: DeliveryStatusFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value !== "all") next.set("status", value);
        else next.delete("status");
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <div style={{ marginBottom: "1.5rem" }}>
        {state?.targetUrl && (
          <Tooltip title={state.targetUrl}>
            <TargetUrlText>{state.targetUrl}</TargetUrlText>
          </Tooltip>
        )}
        <CardMetaRow>
          <Typography variant="body2">
            Subscription ID
          </Typography>
          <CopyableText text={subscriptionId}>{subscriptionId}</CopyableText>
        </CardMetaRow>
        {ownerUserId && (
          <CardMetaRow>
            <Typography variant="body2">
              Owner
            </Typography>
            {state?.userName && (
              <Typography>{state.userName}</Typography>
            )}
            <CopyableText text={ownerUserId}>{ownerUserId}</CopyableText>
          </CardMetaRow>
        )}
      </div>
      <DeliveriesAuditFilters value={statusFilter} onChange={handleStatusChange} />
      <DeliveriesAuditList
        deliveries={deliveries}
        isLoading={query.isLoading}
        isFiltered={statusFilter !== "all"}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={handlePageChange}
      />
    </PageLayout>
  );
}
